import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardHeader,
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { User, UserRole, UserStatus } from '../../types';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--dark-gray);
`;

const UsersList: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Dialog state
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' | 'confirm' = 'info', onConfirm?: () => void) => {
    setDialog({
      isOpen: true,
      title,
      message,
      type,
      onConfirm
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirm = () => {
    if (dialog.onConfirm) {
      dialog.onConfirm();
    }
    closeDialog();
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsers();
      if (response.success) {
        setUsers(response.data || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showDialog('Error', 'Failed to load users. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    let result = [...users];
    
    // Apply search filter
    if (searchTerm) {
      result = result.filter(
        user => 
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply role filter
    if (roleFilter) {
      result = result.filter(user => user.role === roleFilter);
    }
    
    // Apply status filter
    if (statusFilter) {
      result = result.filter(user => user.status === statusFilter);
    }
    
    setFilteredUsers(result);
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getStatusVariant = (status: UserStatus): 'success' | 'danger' | 'warning' => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'warning';
      case 'Suspended': return 'danger';
      default: return 'warning';
    }
  };

  const handleStatusUpdate = async (userId: string, newStatus: string, userName: string) => {
    try {
      const response = await apiService.updateUserStatus(userId, newStatus);
      if (response.success) {
        showDialog(
          'Success', 
          `User "${userName}" status updated to ${newStatus} successfully.`, 
          'success'
        );
        // Reload users to get updated data
        await loadUsers();
      } else {
        showDialog('Error', 'Failed to update user status. Please try again.', 'error');
      }
    } catch (error: any) {
      console.error('Error updating user status:', error);
      const errorMessage = error.message || 'Failed to update user status. Please try again.';
      showDialog('Error', errorMessage, 'error');
    }
  };

  const confirmStatusUpdate = (userId: string, newStatus: string, userName: string, currentStatus: string) => {
    const action = newStatus === 'Active' ? 'activate' : newStatus.toLowerCase();
    const message = `Are you sure you want to ${action} user "${userName}"? This will ${newStatus === 'Active' ? 'allow' : 'prevent'} them from signing in to the system.`;
    
    showDialog(
      'Confirm Status Update',
      message,
      'confirm',
      () => handleStatusUpdate(userId, newStatus, userName)
    );
  };

  // Get unique roles for filter
  const uniqueRoles = Array.from(new Set(users.map(user => user.role)));

  // Format date
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if current user has admin privileges
  const canManageUsers = user?.role === 'MD' || user?.role === 'TD';

  if (loading) {
    return <div>Loading users...</div>;
  }

  return (
    <div>
      <PageHeader>
        <h1 className="page-title">Staff & User Management</h1>
        {canManageUsers && (
          <ActionButtons>
            <Button as={Link} to="/users/new">
              + Add User
            </Button>
            <OutlineButton>
              Excel
            </OutlineButton>
            <OutlineButton>
              PDF
            </OutlineButton>
          </ActionButtons>
        )}
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <Input 
            type="text" 
            placeholder="Search users..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <FilterGroup>
          <label>Role:</label>
          <Select 
            value={roleFilter} 
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="">All Roles</option>
            {uniqueRoles.map(role => (
              <option key={role} value={role}>{role}</option>
            ))}
          </Select>
        </FilterGroup>

        <FilterGroup>
          <label>Status:</label>
          <Select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Suspended">Suspended</option>
          </Select>
        </FilterGroup>

        <Button onClick={() => {
          setSearchTerm('');
          setRoleFilter('');
          setStatusFilter('');
        }}>
          Reset Filters
        </Button>
      </FilterSection>

      <Card>
        <CardHeader>
          <h3>Users ({filteredUsers.length})</h3>
        </CardHeader>
        <CardBody>
          {filteredUsers.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Email</TableHeader>
                    <TableHeader>Role</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Last Login</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {currentItems.map((user) => (
                    <TableRow key={user._id || user.id}>
                      <TableCell>
                        <Link to={`/users/${user._id || user.id}`}>{user.name}</Link>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.role}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(user.status)}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(user.lastLogin)}</TableCell>
                      <TableCell>
                        <FlexContainer gap="0.5rem">
                          <OutlineButton as={Link} to={`/users/${user._id || user.id}`} title="View Details">
                            View
                          </OutlineButton>
                          
                          {canManageUsers && (
                            <>
                              <OutlineButton as={Link} to={`/users/edit/${user._id || user.id}`} title="Edit User">
                                Edit
                              </OutlineButton>
                              {user.status === 'Active' ? (
                                <Button 
                                  title="Suspend User" 
                                  style={{ backgroundColor: 'var(--danger-color)' }}
                                  onClick={() => {
                                    const userId = user._id || user.id;
                                    if (userId) {
                                      confirmStatusUpdate(userId, 'Suspended', user.name, user.status);
                                    }
                                  }}
                                >
                                  Suspend
                                </Button>
                              ) : (
                                <Button 
                                  title="Activate User" 
                                  style={{ backgroundColor: 'var(--success-color)' }}
                                  onClick={() => {
                                    const userId = user._id || user.id;
                                    if (userId) {
                                      confirmStatusUpdate(userId, 'Active', user.name, user.status);
                                    }
                                  }}
                                >
                                  Activate
                                </Button>
                              )}
                            </>
                          )}
                        </FlexContainer>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          ) : (
            <NoDataMessage>
              <p>No users found matching your filters.</p>
            </NoDataMessage>
          )}

          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </PageButton>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                <PageButton
                  key={number}
                  onClick={() => paginate(number)}
                  active={currentPage === number}
                >
                  {number}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </PageButton>
            </Pagination>
          )}
        </CardBody>
      </Card>

      {/* Custom Dialog */}
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        onConfirm={dialog.type === 'confirm' ? handleConfirm : undefined}
        confirmText={dialog.type === 'confirm' ? 'Confirm' : 'OK'}
        cancelText="Cancel"
        showCancel={dialog.type === 'confirm'}
      />
    </div>
  );
};

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

export default UsersList; 